import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate, formatApiError } from '../shared/api';
import { formatMoney, formatDate } from '../shared/formatting';
import { DateInput } from '../shared/DateInput';

type SelectionStatus = 'hold' | 'ready' | 'hitno';

interface SelectionRow {
    id: number;
    company_id: number;
    company_code: string;
    document_number: string;
    document_date: string | null;
    due_date: string | null;
    amount_total: number;
    amount_open: number;
    currency_code: string;
    cost_type: string | null;
    payment_status: string;
    counterparty_name: string | null;
    order_code: string | null;
    sector_id: number | null;
    sector_name: string;
    selection_status: SelectionStatus;
    pay_date: string | null;
    is_urgent: boolean;
    amount_selected: number | null;
    selection_notes: string | null;
    notes: string | null;
    split_count: number;
    split_planned: number;
    is_hidden?: boolean;
}

interface Payload {
    rows: SelectionRow[];
    totals: { hold: number; ready: number; hitno: number; count: number };
    available_cash?: number;
    is_operater?: boolean;
    hidden_count?: number;
    total_count?: number;
    truncated?: boolean;
    companies: { id: number; code: string; name: string }[];
    sectors: { id: number; company_id: number; name_hr: string; name_en: string }[];
    is_superadmin: boolean;
}

const STATUS_META: Record<SelectionStatus, { hr: string; en: string; bg: string; fg: string }> = {
    ready: { hr: 'PLATI', en: 'PAY', bg: '#1f7d4d', fg: '#eafff3' },
    hold: { hr: 'ČEKAJ', en: 'HOLD', bg: '#5a5a66', fg: '#f0f0f4' },
    hitno: { hr: 'HITNO', en: 'URGENT', bg: '#b0341f', fg: '#fff0ec' },
};

const PC = { bg: '#1c1e22', panel: '#24262b', line: '#33363c', ink: '#f2f0ec', dim: '#9a9488', gold: '#c9a227', green: '#3fae6b', red: '#c05a5a', amber: '#d8a24a' };
const pcFmt = (n: number): string => {
    const a = Math.abs(n);
    if (a >= 1e6) return (n / 1e6).toFixed(2).replace('.', ',') + 'M €';
    if (a >= 1e4) return Math.round(n / 1e3) + 'k €';
    return new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(n) + ' €';
};

export function PaymentSelectionModule({
    token,
    locale,
    onBack,
    embedded,
    companyId = '',
}: {
    token: string | null;
    locale: 'hr' | 'en';
    onBack: () => void;
    embedded?: boolean;
    companyId?: string;
}) {
    const [data, setData] = useState<Payload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Odabir tvrtke u zaglavlju Treasuryja vrijedi i ovdje; lokalni izbornik ga moze promijeniti.
    const [companyFilter, setCompanyFilter] = useState(companyId);
    useEffect(() => { setCompanyFilter(companyId); setSectorFilter(''); }, [companyId]);
    const [sectorFilter, setSectorFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<'' | SelectionStatus | 'none'>('');
    const [search, setSearch] = useState('');
    const [busyId, setBusyId] = useState<number | null>(null);
    const [splitFor, setSplitFor] = useState<SelectionRow | null>(null);
    const [detailFor, setDetailFor] = useState<SelectionRow | null>(null);
    const [showHidden, setShowHidden] = useState(false);
    // Rasponi dospijeca (due_date) i datuma placanja (pay_date); prazno = bez filtra.
    const [dueFrom, setDueFrom] = useState('');
    const [dueTo, setDueTo] = useState('');
    const [payFrom, setPayFrom] = useState('');
    const [payTo, setPayTo] = useState('');
    const [sortKey, setSortKey] = useState<'company_code' | 'counterparty_name' | 'document_number' | 'due_date' | 'amount_open' | 'selection_status'>('due_date');
    const [sortDir, setSortDir] = useState<1 | -1>(1);
    const sortBy = (k: typeof sortKey) => { if (sortKey === k) { setSortDir((d) => (d === 1 ? -1 : 1)); } else { setSortKey(k); setSortDir(k === 'amount_open' ? -1 : 1); } };
    const sArrow = (k: typeof sortKey) => sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : '';

    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (companyFilter) params.set('company_id', companyFilter);
            if (sectorFilter) params.set('sector_id', sectorFilter);
            if (statusFilter) params.set('status', statusFilter);
            if (search.trim()) params.set('q', search.trim());
            if (showHidden) params.set('show_hidden', '1');
            if (dueFrom) params.set('due_from', dueFrom);
            if (dueTo) params.set('due_to', dueTo);
            if (payFrom) params.set('pay_from', payFrom);
            if (payTo) params.set('pay_to', payTo);
            const res = await api<Payload>(`/payment-selections?${params.toString()}`, token);
            setData(res);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error');
        } finally {
            setLoading(false);
        }
    }, [token, companyFilter, sectorFilter, statusFilter, search, showHidden, dueFrom, dueTo, payFrom, payTo]);

    async function hide(row: SelectionRow, hidden: boolean) {
        setBusyId(row.id);
        try {
            await mutate(`/payment-selections/${row.id}/hide`, token, 'POST', { hidden });
            await load();
        } catch (e) { setError(e instanceof Error ? e.message : 'Error'); } finally { setBusyId(null); }
    }

    useEffect(() => {
        void load();
    }, [load]);

    const sectorsForCompany = useMemo(() => {
        if (!data) return [];
        if (!companyFilter) return data.sectors;
        return data.sectors.filter((s) => String(s.company_id) === companyFilter);
    }, [data, companyFilter]);

    async function mark(row: SelectionRow, patch: Partial<{ selection_status: SelectionStatus; pay_date: string | null; is_urgent: boolean }>) {
        // Optimistično: instant lokalni update reda (bez full refresha) — server u pozadini.
        setData((d) => d ? { ...d, rows: d.rows.map((r) => r.id === row.id ? {
            ...r,
            selection_status: patch.selection_status ?? r.selection_status,
            pay_date: patch.pay_date !== undefined ? patch.pay_date : r.pay_date,
            is_urgent: patch.is_urgent !== undefined ? patch.is_urgent : r.is_urgent,
        } : r) } : d);
        setError('');
        try {
            await mutate(`/payment-selections/${row.id}/mark`, token, 'POST', {
                selection_status: patch.selection_status ?? row.selection_status,
                pay_date: patch.pay_date !== undefined ? patch.pay_date : row.pay_date,
                is_urgent: patch.is_urgent !== undefined ? patch.is_urgent : row.is_urgent,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error');
            void load(); // revert na stvarno stanje kod greške
        }
    }

    const totals = data?.totals;
    const [batchMsg, setBatchMsg] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const allRows = data?.rows ?? [];
    const markedToday = allRows.filter((r) => r.selection_status === 'ready' || r.selection_status === 'hitno');
    const markedTotal = markedToday.reduce((s, r) => s + (r.amount_selected ?? r.amount_open), 0);
    // Batch = obveze s kvačicom (čisto lokalni odabir, bez servera). Neovisno o PLATI/HITNO/ČEKAJ.
    const batchRows = allRows.filter((r) => selectedIds.has(r.id));
    const batchTotal = batchRows.reduce((s, r) => s + (r.amount_selected ?? r.amount_open), 0);
    const available = data?.available_cash ?? 0;
    const pokrice = batchTotal > 0 ? Math.round((available / batchTotal) * 100) : null;
    const toggleSel = (id: number) => setSelectedIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const allSelected = allRows.length > 0 && allRows.every((r) => selectedIds.has(r.id));
    const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(allRows.map((r) => r.id)));
    async function createBatch() {
        if (!companyFilter) { setBatchMsg(t('Odaberi tvrtku (u zaglavlju) za zbirni nalog.', 'Pick a company (header) for the batch.')); return; }
        const ids = batchRows.map((r) => r.id);
        if (ids.length === 0) { setBatchMsg(t('Odaberi obveze (kvačica) za nalog.', 'Tick invoices for the batch.')); return; }
        setBatchMsg('');
        try {
            const today = new Date().toISOString().slice(0, 10);
            await mutate('/payment-batches', token, 'POST', { company_id: Number(companyFilter), batch_date: today, obligation_ids: ids });
            setBatchMsg(t(`✓ Zbirni nalog kreiran (${ids.length}). Potpiši ga u tabu Nalozi.`, `✓ Batch created (${ids.length}). Sign it in the Batches tab.`)); setSelectedIds(new Set()); void load();
        } catch (e) { setBatchMsg(e instanceof Error ? e.message : 'Greška.'); }
    }
    async function hideSelected() {
        const ids = [...selectedIds];
        if (ids.length === 0) { setBatchMsg(t('Nema odabranih za sakriti.', 'None selected to hide.')); return; }
        setBatchMsg('');
        try {
            await mutate('/payment-selections/hide-bulk', token, 'POST', { ids, hidden: true });
            setBatchMsg(t(`✓ Sakriveno ${ids.length}. Izmiču iz CF-a.`, `✓ Hidden ${ids.length}.`)); setSelectedIds(new Set()); void load();
        } catch (e) { setBatchMsg(e instanceof Error ? e.message : 'Greška.'); }
    }

    return (
        <div style={{ background: PC.bg, color: PC.ink, borderRadius: 14, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                {!embedded ? <button onClick={onBack} style={{ ...btnGhost, background: PC.panel, color: PC.ink, border: `1px solid ${PC.line}` }}>← {t('Natrag', 'Back')}</button> : null}
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>Likvidatura <span style={{ color: PC.dim, fontWeight: 400 }}>— {t('Odabir plaćanja', 'Payment selection')}</span></h2>
                <select value={companyFilter} onChange={(e) => { setCompanyFilter(e.target.value); setSectorFilter(''); }} style={{ marginLeft: 'auto', background: PC.panel, color: PC.ink, border: `1px solid ${PC.line}`, borderRadius: 8, padding: '6px 10px' }}>
                    <option value="">{t('Sve tvrtke', 'All')}</option>
                    {data?.companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                </select>
                {data?.is_operater ? <span style={{ border: `1px solid ${PC.gold}`, color: PC.gold, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>Operater</span> : null}
            </div>

            {/* KPI strip */}
            {totals ? (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <PcKpi label={t('Otvorene obveze', 'Open payables')} value={pcFmt(totals.hold + totals.ready + totals.hitno)} />
                    <PcKpi label={t('Označeno za danas', 'Marked for today')} value={pcFmt(markedTotal)} tone="gold" warn={markedTotal > 0} />
                    <PcKpi label="HITNO" value={pcFmt(totals.hitno)} tone="red" />
                    <PcKpi label={t('Raspoloživo', 'Available')} value={pcFmt(available)} />
                    <PcKpi label={t('Pokriće', 'Coverage')} value={pokrice != null ? pokrice + '%' : '—'} tone={pokrice != null && pokrice < 100 ? 'red' : 'green'} />
                </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,3fr) minmax(240px,1fr)', gap: 12 }}>
                {/* LEFT: obveze table */}
                <div style={{ background: PC.panel, border: `1px solid ${PC.line}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{t('Obveze za plaćanje', 'Payables')}</span>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} style={{ background: PC.bg, color: PC.ink, border: `1px solid ${PC.line}`, borderRadius: 7, padding: '4px 8px', fontSize: 12 }}>
                            <option value="">{t('Svi statusi', 'All')}</option>
                            <option value="ready">PLATI</option><option value="hitno">HITNO</option><option value="hold">ČEKAJ</option>
                        </select>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Traži…', 'Search…')} style={{ background: PC.bg, color: PC.ink, border: `1px solid ${PC.line}`, borderRadius: 7, padding: '4px 8px', fontSize: 12, marginLeft: 'auto', minWidth: 140 }} />
                        <button onClick={() => setShowHidden((v) => !v)} title={t('Nisu knjiženi izvodi / ručno sakriveno', 'Not booked / manually hidden')}
                            style={{ background: showHidden ? PC.gold : 'transparent', color: showHidden ? '#1c1e22' : PC.dim, border: `1px solid ${showHidden ? PC.gold : PC.line}`, borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            {showHidden ? t('← Aktivni', '← Active') : `🙈 ${t('Sakriveni računi', 'Hidden')} (${data?.hidden_count ?? 0})`}
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: PC.dim }}>
                        <span>{t('Dospijeće', 'Due')}</span>
                        <DateInput value={dueFrom} onChange={setDueFrom} placeholder={t('od', 'from')} style={dateFilterStyle} />
                        <span>-</span>
                        <DateInput value={dueTo} onChange={setDueTo} placeholder={t('do', 'to')} style={dateFilterStyle} />
                        <span style={{ marginLeft: 10 }}>{t('Datum plaćanja', 'Pay date')}</span>
                        <DateInput value={payFrom} onChange={setPayFrom} placeholder={t('od', 'from')} style={dateFilterStyle} />
                        <span>-</span>
                        <DateInput value={payTo} onChange={setPayTo} placeholder={t('do', 'to')} style={dateFilterStyle} />
                        {dueFrom || dueTo || payFrom || payTo ? (
                            <button onClick={() => { setDueFrom(''); setDueTo(''); setPayFrom(''); setPayTo(''); }}
                                style={{ background: 'transparent', color: PC.dim, border: `1px solid ${PC.line}`, borderRadius: 7, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>
                                ✕ {t('Očisti datume', 'Clear dates')}
                            </button>
                        ) : null}
                    </div>
                    {error ? <div style={{ background: 'rgba(192,90,90,0.15)', color: PC.red, padding: 8, borderRadius: 7, marginBottom: 8, fontSize: 12 }}>{error}</div> : null}
                    {loading ? <div style={{ color: PC.dim }}>{t('Učitavam…', 'Loading…')}</div> : null}
                    {!loading && data?.truncated ? <div style={{ color: PC.gold, padding: '6px 0', fontSize: 12 }}>{t(`Prikazano ${data.rows.length} od ${data.total_count} obveza (najranije dospijece prvo). Zbrojevi vrijede za svih ${data.total_count}; suzi filter za ostale.`, `Showing ${data.rows.length} of ${data.total_count} payables (earliest due first). Totals cover all ${data.total_count}; narrow the filter for the rest.`)}</div> : null}
                    {!loading && data && data.rows.length === 0 ? <div style={{ color: PC.dim, padding: 16, fontSize: 13 }}>{t('Nema otvorenih obveza za odabir.', 'No open payables.')}</div> : null}
                    {!loading && data && data.rows.length > 0 ? (
                        <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 350px)', minHeight: 380, overflowY: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                                <thead><tr style={{ textAlign: 'left', color: PC.dim, fontSize: 11 }}>
                                    <th style={{ ...pcTh, cursor: 'pointer' }} onClick={toggleAll} title={t('Odaberi/poništi sve PLATI/HITNO', 'Select/clear all PAY/URGENT')}><SelBox on={allSelected} /></th>
                                    <th style={pcTh}></th><th style={{ ...pcTh, cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('company_code')}>{t('Tvrtka', 'Company')}{sArrow('company_code')}</th><th style={{ ...pcTh, cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('counterparty_name')}>{t('Dobavljač', 'Supplier')}{sArrow('counterparty_name')}</th><th style={{ ...pcTh, cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('document_number')}>{t('Dokument', 'Doc')}{sArrow('document_number')}</th>
                                    <th style={{ ...pcTh, cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('due_date')}>{t('Dospijeće', 'Due')}{sArrow('due_date')}</th><th style={{ ...pcTh, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('amount_open')}>{t('Iznos', 'Amount')}{sArrow('amount_open')}</th>
                                    <th style={{ ...pcTh, cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy('selection_status')}>Status{sArrow('selection_status')}</th><th style={pcTh}>{t('Dan', 'Day')}</th><th style={pcTh}></th>
                                </tr></thead>
                                <tbody>
                                    {[...data.rows].sort((a, b) => {
                                        const av = a[sortKey], bv = b[sortKey];
                                        if (typeof av === 'number' || typeof bv === 'number') return sortDir * ((Number(av ?? 0)) - (Number(bv ?? 0)));
                                        return sortDir * String(av ?? '').localeCompare(String(bv ?? ''));
                                    }).map((r) => {
                                        const meta = STATUS_META[r.selection_status];
                                        const dot = r.selection_status === 'ready' ? PC.green : r.selection_status === 'hitno' ? PC.amber : PC.dim;
                                        const overdue = r.due_date ? new Date(r.due_date + 'T00:00:00') < new Date(new Date().toDateString()) : false;
                                        const sel = selectedIds.has(r.id);
                                        return (
                                            <tr key={r.id} style={{ borderTop: `1px solid ${PC.line}`, background: sel ? 'rgba(201,162,39,0.12)' : undefined }}>
                                                <td style={{ ...pcTd, cursor: 'pointer' }} onClick={() => toggleSel(r.id)} title={sel ? t('Ukloni iz naloga', 'Remove from batch') : t('U zbirni nalog', 'Into batch')}><SelBox on={sel} /></td>
                                                <td style={pcTd}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: dot }} /></td>
                                                <td style={{ ...pcTd, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.company_code}</td>
                                                <td style={{ ...pcTd, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline dotted' }} onClick={() => setDetailFor(r)} title={t('Detalji obveze', 'Payable details')}>{r.counterparty_name ?? '—'}</td>
                                                <td style={{ ...pcTd, fontFamily: 'monospace', fontSize: 11, color: PC.dim, cursor: 'pointer' }} onClick={() => setDetailFor(r)}>{r.document_number}</td>
                                                <td style={{ ...pcTd, whiteSpace: 'nowrap', color: overdue ? PC.red : PC.dim }}>{r.due_date ? formatDate(r.due_date, locale) : '—'}</td>
                                                <td style={{ ...pcTd, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{pcFmt(r.amount_open)}</td>
                                                <td style={pcTd}><span style={{ background: meta.bg, color: meta.fg, padding: '2px 7px', borderRadius: 5, fontSize: 10.5, fontWeight: 700 }}>{locale === 'en' ? meta.en : meta.hr}</span></td>
                                                <td style={{ ...pcTd, minWidth: 96 }}><DateInput value={r.pay_date ?? ''} onChange={(iso) => void mark(r, { pay_date: iso || null })} /></td>
                                                <td style={pcTd}>
                                                    <div style={{ display: 'flex', gap: 3 }}>
                                                        <button disabled={busyId === r.id} onClick={() => void mark(r, { selection_status: r.selection_status === 'ready' ? 'hold' : 'ready' })} style={pcChip(PC.green, r.selection_status === 'ready')}>PLATI</button>
                                                        <button disabled={busyId === r.id} onClick={() => void mark(r, { selection_status: r.selection_status === 'hitno' ? 'hold' : 'hitno' })} style={pcChip(PC.amber, r.selection_status === 'hitno')}>HITNO</button>
                                                        <button disabled={busyId === r.id} onClick={() => void mark(r, { selection_status: 'hold' })} style={pcChip(PC.dim, r.selection_status === 'hold')}>ČEKAJ</button>
                                                        <button disabled={busyId === r.id} onClick={() => setSplitFor(r)} style={pcChip(PC.gold, false)} title={t('Razdijeli', 'Split')}>✂</button>
                                                        <button disabled={busyId === r.id} onClick={() => void hide(r, !r.is_hidden)} style={pcChip(PC.dim, !!r.is_hidden)} title={r.is_hidden ? t('Vrati u aktivne', 'Unhide') : t('Sakrij (nije knjižen izvod)', 'Hide (not booked)')}>{r.is_hidden ? '↩' : '🙈'}</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>

                {/* RIGHT: zbirni nalog cart */}
                <div style={{ background: PC.panel, border: `1px solid ${PC.line}`, borderRadius: 10, padding: 12, alignSelf: 'flex-start' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{t('Zbirni nalog (u izradi)', 'Batch (draft)')}</div>
                    <div style={{ fontSize: 11, color: PC.dim, marginBottom: 8 }}>{t('Kvačicom odaberi obveze koje idu u nalog.', 'Tick invoices to include.')} {markedToday.length > 0 ? <button onClick={toggleAll} style={{ background: 'transparent', border: 'none', color: PC.gold, cursor: 'pointer', fontSize: 11, padding: 0 }}>{allSelected ? t('poništi', 'clear') : t('sve', 'all')}</button> : null}</div>
                    <div style={{ maxHeight: 'calc(100vh - 560px)', minHeight: 160, overflowY: 'auto', marginBottom: 8 }}>
                        {batchRows.length === 0 ? <div style={{ color: PC.dim, fontSize: 12 }}>{t('Nijedna obveza odabrana.', 'No invoice selected.')}</div> :
                            batchRows.slice(0, 40).map((r) => (
                                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '4px 0', borderBottom: `1px solid ${PC.line}`, fontSize: 12 }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.counterparty_name ?? r.document_number}</span>
                                    <span style={{ color: r.selection_status === 'hitno' ? PC.amber : PC.ink, whiteSpace: 'nowrap' }}>{pcFmt(r.amount_open)}</span>
                                </div>
                            ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginBottom: 6 }}><span>{t('Ukupno', 'Total')} ({batchRows.length})</span><span>{pcFmt(batchTotal)}</span></div>
                    {/* gauge */}
                    <div style={{ height: 6, borderRadius: 3, background: PC.line, overflow: 'hidden', marginBottom: 4 }}>
                        <div style={{ width: `${Math.min(100, pokrice ?? 0)}%`, height: '100%', background: pokrice != null && pokrice < 100 ? PC.red : PC.green }} />
                    </div>
                    <div style={{ fontSize: 11, color: PC.dim, marginBottom: 10 }}>{t('Raspoloživo', 'Available')} {pcFmt(available)} vs {t('odabrano', 'selected')} {pcFmt(batchTotal)}</div>
                    <button onClick={() => void createBatch()} disabled={!data?.is_operater || batchRows.length === 0} style={{ width: '100%', padding: '9px', borderRadius: 8, border: 0, background: (!data?.is_operater || batchRows.length === 0) ? PC.line : PC.green, color: '#fff', fontWeight: 700, cursor: (!data?.is_operater || batchRows.length === 0) ? 'default' : 'pointer' }}>
                        {t('Kreiraj zbirni nalog', 'Create batch')} {batchRows.length > 0 ? `(${batchRows.length})` : ''}
                    </button>
                    <button onClick={() => void hideSelected()} disabled={batchRows.length === 0} style={{ width: '100%', padding: '7px', marginTop: 6, borderRadius: 8, border: `1px solid ${PC.line}`, background: 'transparent', color: PC.dim, opacity: batchRows.length ? 1 : 0.45, fontWeight: 600, cursor: batchRows.length ? 'pointer' : 'default' }}>
                        🙈 {t('Sakrij odabrane', 'Hide selected')} {batchRows.length > 0 ? `(${batchRows.length})` : ''}
                    </button>
                    {!data?.is_operater ? <div style={{ fontSize: 11, color: PC.dim, marginTop: 6 }}>{t('Nalog kreira ovlašteni operater.', 'Only an operator can create batches.')}</div> : null}
                    {batchMsg ? <div style={{ fontSize: 12, color: batchMsg.startsWith('✓') ? PC.green : PC.red, marginTop: 6 }}>{batchMsg}</div> : null}
                    <div style={{ fontSize: 11, color: PC.dim, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${PC.line}` }}>{t('Računi iznad praga trebaju 2. potpis (tab "2. potpis").', 'Invoices above the threshold need a 2nd signature ("2nd sign" tab).')}</div>
                </div>
            </div>

            {/* bottom alerts */}
            {totals ? (() => {
                const todayIso = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
                const overdueCnt = allRows.filter((r) => r.due_date && r.due_date < todayIso).length;
                return (
                    <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 12, color: PC.dim }}>
                        {overdueCnt > 0 ? <span style={{ color: PC.amber }}>⚠ {t('Dospjele obveze u listi', 'Overdue in list')}: {overdueCnt}</span> : <span style={{ color: PC.green }}>✓ {t('Bez dospjelih u listi', 'No overdue in list')}</span>}
                        <span>{t('Nepokrivena rezerva', 'Uncovered reserve')}: <strong style={{ color: pokrice != null && pokrice < 100 ? PC.red : PC.green }}>{pokrice != null && pokrice < 100 ? 'DA' : 'NE'}</strong></span>
                    </div>
                );
            })() : null}

            {detailFor ? (
                <ObligationDrawer row={detailFor} company={data?.companies.find((c) => c.id === detailFor.company_id)} locale={locale} onClose={() => setDetailFor(null)} />
            ) : null}

            {splitFor ? (
                <SplitModal row={splitFor} token={token} locale={locale} onClose={() => setSplitFor(null)} onSaved={() => { setSplitFor(null); void load(); }} setError={setError} />
            ) : null}
        </div>
    );
}

function PcKpi({ label, value, tone, warn }: { label: string; value: string; tone?: 'green' | 'red' | 'gold'; warn?: boolean }) {
    const c = tone === 'green' ? PC.green : tone === 'red' ? PC.red : tone === 'gold' ? PC.gold : PC.ink;
    return (
        <div style={{ background: PC.panel, border: `1px solid ${warn ? PC.gold : PC.line}`, borderRadius: 10, padding: '9px 12px', minWidth: 120, flex: 1 }}>
            <div style={{ fontSize: 11, color: PC.dim, marginBottom: 3 }}>{label} {warn ? '⚠' : ''}</div>
            <div style={{ fontSize: 19, fontWeight: 700, color: c }}>{value}</div>
        </div>
    );
}

function SelBox({ on }: { on: boolean }) {
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 17, height: 17, borderRadius: 4, border: `1.5px solid ${on ? PC.gold : PC.dim}`, background: on ? PC.gold : 'transparent', color: '#1c1e22', fontSize: 12, fontWeight: 900, lineHeight: 1 }}>
            {on ? '✓' : ''}
        </span>
    );
}

// Bocni panel s podacima obveze: tko je duznik (tvrtka grupe), dobavljac, dokument i stanje odabira.
function ObligationDrawer({ row, company, locale, onClose }: { row: SelectionRow; company?: { id: number; code: string; name: string }; locale: 'hr' | 'en'; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const d = (s: string | null) => (s ? formatDate(s, locale) : '—');
    const items: [string, React.ReactNode][] = [
        [t('Obveznik plaćanja', 'Payer'), <span style={{ fontWeight: 700, color: PC.gold }}>{company?.name ?? row.company_code} ({row.company_code})</span>],
        [t('Dobavljač', 'Supplier'), row.counterparty_name ?? '—'],
        [t('Dokument', 'Document'), <span style={{ fontFamily: 'monospace' }}>{row.document_number}</span>],
        [t('Datum dokumenta', 'Document date'), d(row.document_date)],
        [t('Dospijeće', 'Due'), d(row.due_date)],
        [t('Iznos računa', 'Invoice amount'), formatMoney(row.amount_total, locale) + ' ' + row.currency_code],
        [t('Otvoreno', 'Open'), formatMoney(row.amount_open, locale) + ' ' + row.currency_code],
        [t('Status plaćanja', 'Payment status'), row.payment_status],
        [t('Vrsta troška', 'Cost type'), row.cost_type ?? '—'],
        [t('Sektor', 'Sector'), row.sector_name || '—'],
        [t('Šifra narudžbe', 'Order code'), row.order_code ?? '—'],
        [t('Odabir', 'Selection'), `${STATUS_META[row.selection_status][locale === 'en' ? 'en' : 'hr']}${row.pay_date ? ' · ' + d(row.pay_date) : ''}${row.amount_selected != null ? ' · ' + formatMoney(row.amount_selected, locale) : ''}`],
        [t('Razdioba', 'Split'), row.split_count > 0 ? `${row.split_count} · ${formatMoney(row.split_planned, locale)}` : '—'],
        [t('Napomena odabira', 'Selection note'), row.selection_notes ?? '—'],
        [t('Napomena', 'Note'), row.notes ?? '—'],
    ];
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 }} onClick={onClose}>
            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 400, maxWidth: '92vw', background: PC.panel, borderLeft: `1px solid ${PC.line}`, color: PC.ink, padding: 18, overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{t('Obveza', 'Payable')}</span>
                    <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', color: PC.dim, border: `1px solid ${PC.line}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>✕</button>
                </div>
                {items.map(([k, v]) => (
                    <div key={k} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 8, padding: '6px 0', borderTop: `1px solid ${PC.line}`, fontSize: 12.5 }}>
                        <span style={{ color: PC.dim }}>{k}</span><span>{v}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

const pcTh: React.CSSProperties = { padding: '4px 6px', fontWeight: 600 };
const pcTd: React.CSSProperties = { padding: '5px 6px' };
const pcChip = (color: string, active: boolean): React.CSSProperties => ({ background: active ? color : 'transparent', color: active ? '#fff' : color, border: `1px solid ${color}`, borderRadius: 5, padding: '2px 6px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer' });


function SplitModal({
    row, token, locale, onClose, onSaved, setError,
}: {
    row: SelectionRow;
    token: string | null;
    locale: 'hr' | 'en';
    onClose: () => void;
    onSaved: () => void;
    setError: (s: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [lines, setLines] = useState<{ amount: string; date: string }[]>([
        { amount: String(row.amount_open), date: row.pay_date ?? '' },
    ]);
    const [saving, setSaving] = useState(false);

    const sum = lines.reduce((a, l) => a + (parseFloat(l.amount) || 0), 0);
    const diff = Math.round((row.amount_open - sum) * 100) / 100;

    async function save() {
        setSaving(true);
        try {
            await mutate(`/payment-selections/${row.id}/split`, token, 'POST', {
                payments: lines
                    .filter((l) => l.date && (parseFloat(l.amount) || 0) !== 0)
                    .map((l) => ({ planned_amount: parseFloat(l.amount), planned_date: l.date })),
            });
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : formatApiError({}));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div style={overlay} onClick={onClose}>
            <div style={modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginTop: 0 }}>✂ {t('Razdioba plaćanja', 'Split payment')}</h3>
                <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>
                    {row.counterparty_name} · {row.document_number} · {t('otvoreno', 'open')} {formatMoney(row.amount_open, locale)}
                </div>
                {lines.map((l, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <input type="number" step="0.01" value={l.amount} onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} style={{ ...input, width: 130 }} placeholder={t('Iznos', 'Amount')} />
                        <DateInput value={l.date} onChange={(iso) => setLines((p) => p.map((x, j) => j === i ? { ...x, date: iso } : x))} />
                        {lines.length > 1 ? <button onClick={() => setLines((p) => p.filter((_, j) => j !== i))} style={miniBtn('#7a3a2a')}>✕</button> : null}
                    </div>
                ))}
                <button onClick={() => setLines((p) => [...p, { amount: String(Math.max(diff, 0)), date: '' }])} style={btnGhost}>+ {t('Dodaj ratu', 'Add line')}</button>
                <div style={{ marginTop: 10, fontSize: 13, color: Math.abs(diff) < 0.005 ? '#1f7d4d' : '#a06a1f' }}>
                    {t('Zbroj', 'Sum')}: {formatMoney(sum, locale)} · {t('razlika', 'diff')}: {formatMoney(diff, locale)}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={btnGhost}>{t('Odustani', 'Cancel')}</button>
                    <button disabled={saving} onClick={() => void save()} style={{ ...btnGhost, color: '#fff', background: '#1f7d4d', borderColor: '#1f7d4d' }}>{saving ? '…' : t('Spremi razdiobu', 'Save split')}</button>
                </div>
            </div>
        </div>
    );
}

function TotalChip({ label, value, color }: { label: string; value: string; color: string }) {
    return (
        <div style={{ background: '#fbf7ee', border: `1px solid ${color}`, borderRadius: 10, padding: '8px 14px', minWidth: 110 }}>
            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color }}>{value}</div>
        </div>
    );
}

const input: React.CSSProperties = { background: '#fbf7ee', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 10px', fontSize: 13 };
const btnGhost: React.CSSProperties = { background: '#fffdf6', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13 };
const dateFilterStyle: React.CSSProperties = { background: PC.bg, color: PC.ink, border: `1px solid ${PC.line}`, borderRadius: 7, padding: '4px 8px', fontSize: 12, width: 100 };
const miniBtn = (bg: string): React.CSSProperties => ({ background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 });
const th: React.CSSProperties = { padding: '9px 10px', fontWeight: 600, fontSize: 12, opacity: 0.85 };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal: React.CSSProperties = { background: '#ffffff', border: '1px solid #d8cdb2', borderRadius: 12, padding: 22, width: 440, maxWidth: '92vw', color: '#2f3337' };
